/** The window never shrinks below this (four frames) and never grows past `max`. */
export const UPLOAD_WINDOW_MIN = 64 * 1024;
export const UPLOAD_WINDOW_INITIAL = 256 * 1024;
/** Delay added by queueing, over the best round trip seen, that the window aims for (ms). */
export const UPLOAD_DELAY_TARGET_MS = 40;
const GROW = 0.5;
const SHRINK = 0.8;

/**
 * How many bytes an upload may have in flight, from how long the server takes to acknowledge them. The best round
 * trip seen is the empty-queue baseline; while an acknowledgement arrives close to it the window grows (by up to half
 * of what was acknowledged, less the nearer it is to the target), and once it is late by the target or more the window
 * shrinks, at most once per round trip. A server that holds acknowledgements back, because terminal output is waiting,
 * and a congested link therefore both close the window by themselves, and an idle fast link opens it up to `max`.
 */
export class UploadWindow {
  private win = UPLOAD_WINDOW_INITIAL;
  private base = Infinity;
  private ackedTo = 0;
  private lastShrink = -Infinity;
  private ends: number[] = [];
  private times: number[] = [];
  private head = 0;

  constructor(private max: number) {}

  /** Bytes that may be unacknowledged now. */
  get bytes(): number { return Math.floor(Math.min(this.win, this.max)); }

  /** Forget the frames in flight: a new upload, or a resume from the server's offset. The baseline is kept. */
  reset(offset = 0) {
    this.win = UPLOAD_WINDOW_INITIAL;
    this.ackedTo = offset;
    this.ends.length = 0; this.times.length = 0; this.head = 0;
    this.lastShrink = -Infinity;
  }

  /** A frame ending at file offset `end` was handed to the transport at `now` (ms). */
  onSend(end: number, now: number) {
    this.ends.push(end); this.times.push(now);
  }

  /** The server says it has everything below `received`. */
  onAck(received: number, now: number) {
    let t = -1;
    while (this.head < this.ends.length && this.ends[this.head] <= received) t = this.times[this.head++];
    if (this.head > 256) { this.ends.splice(0, this.head); this.times.splice(0, this.head); this.head = 0; }
    const acked = received - this.ackedTo;
    this.ackedTo = Math.max(this.ackedTo, received);
    if (t < 0 || acked <= 0) return;
    const rtt = Math.max(0, now - t);
    this.base = Math.min(this.base, rtt);
    const queued = rtt - this.base;
    if (queued < UPLOAD_DELAY_TARGET_MS) {
      this.win += acked * GROW * (1 - queued / UPLOAD_DELAY_TARGET_MS);
    } else if (now - this.lastShrink >= rtt) {
      this.win *= SHRINK;
      this.lastShrink = now;
    }
    this.win = Math.min(this.max, Math.max(UPLOAD_WINDOW_MIN, this.win));
  }
}
